import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { ApSchedulerBuildStage } from '../sic-lib/enums/ApSchedulerBuildStage';
import { ApPlanBase } from '../sic-lib/types/ActionPlan';
import { ApSchedulerContact } from '../sic-lib/types/ApSchedulerContact';
import { hasDistinctContactId, hasMatchingContactId } from '../sic-lib/utils/functionalFilters';

export interface CommunicatorState {
    buildStage: ApSchedulerBuildStage;
    contacts: ApSchedulerContact[];
    editContactId: number;
    template: ApPlanBase | null;
}

const initialState: CommunicatorState = {
    buildStage: ApSchedulerBuildStage.edit,
    contacts: [],
    editContactId: -1,
    template: null,
};

const communicatorSlice = createSlice({
    name: 'communicator',
    initialState,
    reducers: {
        setBuildStage: (state, action) => {
            state.buildStage = action.payload;
        },
        setContacts: (state, action) => {
            state.contacts = [...action.payload];
        },
        removeContacts: (state, action) => {
            state.contacts = state.contacts.filter(hasDistinctContactId(action.payload));
        },
        setEditContact: (state, action) => {
            state.editContactId = action.payload;
        },
        applyContactChanges: (
            state,
            action: PayloadAction<{ contactId: number; changes: Partial<ApSchedulerContact> }>,
        ) => {
            state.contacts = state.contacts.map(cnt =>
                cnt.contactId === action.payload.contactId
                    ? { ...cnt, ...action.payload.changes }
                    : cnt,
            );
            state.editContactId = -1;
        },
        applyMultiContactChanges: (state, action) => {
            state.contacts = state.contacts.map(cnt => {
                const matchedContact = action.payload.filter(
                    hasMatchingContactId(cnt.contactId),
                )[0];
                return matchedContact || cnt;
            });
        },
        setTemplate: (state, action) => {
            state.template = action.payload;
        },
        resetCommunicatorState: state => {
            state.template = null;
            state.contacts = [];
            state.buildStage = ApSchedulerBuildStage.edit;
        },
    },
});

export const {
    setBuildStage,
    setContacts,
    removeContacts,
    setEditContact,
    applyContactChanges,
    applyMultiContactChanges,
    setTemplate,
    resetCommunicatorState,
} = communicatorSlice.actions;

export default communicatorSlice.reducer;
